'use client'

import { useEffect, useState } from 'react'
import Modal from './Modal'
import Link from 'next/link'
import { SocialMediaIcon } from './SocialMediaIcon'
import { type getDictionary } from '@/utils/loadLocaleData'
import { Buttton } from '../common'

import styles from '@/styles/components/URLShareDialog.module.scss'

type URLShareDialogProps = {
    text: string,
    url: string,
    via?: string
    dictionary?: any;
}

export const URLShareDialog = ({ text, url, via = '', dictionary = { label: 'Share', linkToShare: 'Link to Share', copied: 'Copied' } }: URLShareDialogProps) => {

    const [isTextCopied, setIsTextCopied] = useState<boolean>(false)
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false)

    const encodedText = encodeURIComponent(text)
    const encodedUrl = encodeURIComponent(url)

    const handleCopyLinkClick = () => {
        navigator.clipboard.writeText(url + '?source=share-link')
        setIsTextCopied(true)
        setTimeout(() => setIsTextCopied(false), 1000);
    }

    const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'S') {
            event.preventDefault()
            setIsModalVisible(true)
        } else {
            setIsModalVisible(false)
        }
    }

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [])

    const socialPlatforms = [
        {
            name: 'X',
            url: `https://x.com/intent/post?text=${encodedText}&url=${encodedUrl}?source=share-x${via ? `&via=${encodeURIComponent(via)}` : ''}`,
            icon: 'x'
        },
        /*{
            name: 'Facebook',
            url: `https://x.com/intent/post?text=${encodedText}&url=${encodedUrl}?source=share-x${via ? `&via=${encodeURIComponent(via)}` : ''}`,
            icon: 'facebook'
        },*/
        {
            name: 'Telegram',
            url: `https://t.me/share/url?text=${encodedText}&url=${encodedUrl}?source=share-telegram`,
            icon: 'telegram'

        },
        {
            name: 'WhatsApp',
            url: `https://api.whatsapp.com/send?text=${encodedText} ${encodedUrl}?source=share-wa`,
            icon: 'whatsapp'
        }
    ]

    return (
        <div className={styles.share}>
            <Buttton className={styles.shareButton} onClick={() => setIsModalVisible(true)}>
                <svg height="16" strokeLinejoin="round" viewBox="0 0 16 16" width="16" style={{ color: "currentcolor" }}>
                    <path fillRule="evenodd" clipRule="evenodd" d="M15 11.25V10.5H13.5V11.25V12.75C13.5 13.1642 13.1642 13.5 12.75 13.5H3.25C2.83579 13.5 2.5 13.1642 2.5 12.75L2.5 3.25C2.5 2.83579 2.83579 2.5 3.25 2.5H5.75H6.5V1H5.75H3.25C2.00736 1 1 2.00736 1 3.25V12.75C1 13.9926 2.00736 15 3.25 15H12.75C13.9926 15 15 13.9926 15 12.75V11.25ZM15 5.5L10.5 1V4C7.46243 4 5 6.46243 5 9.5V10L5.05855 9.91218C6.27146 8.09281 8.31339 7 10.5 7V10L15 5.5Z" fill="currentColor"></path>
                </svg>
                <span>{dictionary?.label}</span>
            </Buttton>
            <Modal variant='responsive' isVisiable={isModalVisible} onClose={() => setIsModalVisible(false)}>
                <div className={styles.shareDialog}>
                    <section>
                        <h3>{dictionary?.label}</h3>
                        <ul className={styles.platformList}>
                            {socialPlatforms.map((platform: any) => {
                                return <li key={`share-${platform.name}`} className={styles.platformListItem}>
                                    <SocialMediaIcon platform={platform.icon} width={48} height={48} className={styles.platformListItemIcon} />
                                    <Link href={platform.url} target='_blank' rel='noopener noreferrer' className={styles.platformListItemLink} prefetch={false}>
                                        <span>{platform.name}</span>
                                    </Link>
                                </li>
                            })}
                        </ul>
                    </section>

                    <section>
                        <h4>{dictionary?.linkToShare}</h4>

                        <div style={{ display: 'flex' }}>
                            <button className={styles.shareLinkCopy} onClick={handleCopyLinkClick}>
                                <span>{url}</span>
                                <div className={styles.copy}>
                                    {isTextCopied ?
                                        <svg height="16" strokeLinejoin="round" viewBox="0 0 16 16" width="16"><path fillRule="evenodd" clipRule="evenodd" d="M15.5607 3.99999L15.0303 4.53032L6.23744 13.3232C5.55403 14.0066 4.44599 14.0066 3.76257 13.3232L4.2929 12.7929L3.76257 13.3232L0.969676 10.5303L0.439346 9.99999L1.50001 8.93933L2.03034 9.46966L4.82323 12.2626C4.92086 12.3602 5.07915 12.3602 5.17678 12.2626L13.9697 3.46966L14.5 2.93933L15.5607 3.99999Z" fill="green"></path></svg> :
                                        <svg height="16" strokeLinejoin="round" viewBox="0 0 16 16" width="16"><path fillRule="evenodd" clipRule="evenodd" d="M2.75 0.5C1.7835 0.5 1 1.2835 1 2.25V9.75C1 10.7165 1.7835 11.5 2.75 11.5H3.75H4.5V10H3.75H2.75C2.61193 10 2.5 9.88807 2.5 9.75V2.25C2.5 2.11193 2.61193 2 2.75 2H8.25C8.38807 2 8.5 2.11193 8.5 2.25V3H10V2.25C10 1.2835 9.2165 0.5 8.25 0.5H2.75ZM7.75 4.5C6.7835 4.5 6 5.2835 6 6.25V13.75C6 14.7165 6.7835 15.5 7.75 15.5H13.25C14.2165 15.5 15 14.7165 15 13.75V6.25C15 5.2835 14.2165 4.5 13.25 4.5H7.75ZM7.5 6.25C7.5 6.11193 7.61193 6 7.75 6H13.25C13.3881 6 13.5 6.11193 13.5 6.25V13.75C13.5 13.8881 13.3881 14 13.25 14H7.75C7.61193 14 7.5 13.8881 7.5 13.75V6.25Z" fill="currentColor"></path></svg>
                                    }
                                    {isTextCopied && <span className={styles.copiedText}>{dictionary?.copied as string}</span>}
                                </div>
                            </button>
                        </div>
                    </section>
                </div>
            </Modal>
        </div>
    )
}

export default URLShareDialog